import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { api, clearSession } from './api';

const CTX = createContext(null);
const STORAGE_KEY = 'root_informatica_v1';

export const today = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s) { const d = JSON.parse(s); if (d?.config?.fechaManual) return d.config.fechaManual; }
  } catch {}
  return new Date().toISOString().slice(0, 10);
};
export const fmt = (d) => new Date(d).toLocaleDateString('es-ES');
export function calcLineas(lineas) {
  const base = lineas.reduce((s,l) => s + l.cant*l.precio, 0);
  const iva  = lineas.reduce((s,l) => s + l.cant*l.precio*l.iva/100, 0);
  return { base, iva, total: base+iva };
}
export function formatEur(n) {
  return new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR'}).format(n||0);
}

// ── Ajuste de stock por venta (Facturas y Tickets, no Presupuestos/Proformas/Pedidos cliente) ──
// Empareja cada línea con un artículo del catálogo por nombre exacto — así es como se
// rellenan las líneas al elegir del buscador (LineasEditor). Líneas escritas a mano que no
// coincidan con ningún artículo no afectan al stock.
const COLS_VENTA = ['facturas', 'tickets'];

function matchArticuloPorNombre(articulos, nombre) {
  const n = String(nombre ?? '').trim().toLowerCase();
  if (!n) return null;
  return articulos.find(a => (a.nombre||'').trim().toLowerCase() === n);
}

function cantidadesPorArticulo(articulos, lineas) {
  const m = new Map();
  (lineas||[]).forEach(l => {
    const a = matchArticuloPorNombre(articulos, l.desc);
    if (a) m.set(a.id, (m.get(a.id)||0) + (parseFloat(l.cant)||0));
  });
  return m;
}

// Compara líneas de antes/después de una Factura/Ticket y devuelve los artículos cuyo
// stock cambia (ya con el nuevo valor calculado), listos para persistir.
function calcularAjusteStock(articulos, lineasAntes, lineasDespues) {
  const antes = cantidadesPorArticulo(articulos, lineasAntes);
  const despues = cantidadesPorArticulo(articulos, lineasDespues);
  const ids = new Set([...antes.keys(), ...despues.keys()]);
  const cambios = [];
  ids.forEach(id => {
    const delta = (despues.get(id)||0) - (antes.get(id)||0);
    if (!delta) return;
    const art = articulos.find(a => a.id === id);
    if (art) cambios.push({ ...art, stock: Math.max(0, (art.stock||0) - delta) });
  });
  return cambios;
}

function emptyData() {
  return {
    config:{
      nombre:'Root Informática', cif:'B12345678',
      direccion:'C/ Informática 12, 48001 Bilbao',
      telefono:'944 123 456', whatsapp:'', email:'info@rootinformatica.es',
      web:'rootinformatica.es', iva:21,
      sigTicket:188, sigFactura:125, sigProforma:19,
      sigPresupuesto:37, sigPedido:90, sigReparacion:313, sigPedidoCliente:1,
      ticketUsarLogo:true, facturaUsarLogo:true,
      ticketCabecera:'', ticketPie:'', fechaManual:'',
    },
    clientes:[], articulos:[], tickets:[], facturas:[],
    proformas:[], presupuestos:[], pedidos:[], reparaciones:[], pedidosCliente:[], proveedores:[], facturasProveedor:[], apuntes:[], espaciosTaller:[],
  };
}

const COLS = ['clientes','articulos','tickets','facturas','proformas','presupuestos','pedidos','reparaciones','pedidosCliente','proveedores','facturasProveedor','apuntes','espaciosTaller'];

// addItem/replaceItem/deleteItem/updateConfig actualizan el estado local ANTES de confirmar
// con el servidor (UI optimista) — si la llamada al servidor falla (sesión caducada, red,
// error del servidor) hay que avisar, si no el cambio se pierde en silencio: parece guardado
// en esta pantalla pero nunca llegó a la base de datos, y desaparece al recargar o en otro
// ordenador.
function avisarErrorSync(contexto, e) {
  console.error(contexto, e);
  alert(`⚠️ No se pudo guardar en el servidor (${contexto}): ${e.message}\n\nEl cambio puede haberse perdido. Vuelve a intentarlo — si sigue fallando, puede que la sesión haya caducado (recarga la página) o falle la conexión.`);
}

export function StoreProvider({ children }) {
  const [data,    setData]    = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [auth,    setAuth]    = useState({ ok: !!localStorage.getItem('ri_loggedin'), nombre: localStorage.getItem('ri_user')||'', rol: localStorage.getItem('ri_rol')||'admin' });

  // ── Cargar todo desde el servidor ──
  const loadAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [cfg, ...colData] = await Promise.all([
        api.getConfig(),
        ...COLS.map(c => api.getAll(c)),
      ]);
      const d = { config: cfg };
      COLS.forEach((c,i) => { d[c] = colData[i]; });
      // Limpiar campo interno que causaba la llave visible
      ['facturas','proformas','presupuestos','tickets'].forEach(k => {
        if (d[k]) d[k] = d[k].map(({ _origenPresupuesto, ...rest }) => rest);
      });
      setData(d);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
    } catch(e) {
      console.error('Error cargando datos:', e);
      // Fallback a cache local
      try {
        const cached = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (cached) setData(cached);
      } catch {}
    }
    if (!silent) setLoading(false);
  }, []);

  // ── Login ──
  const login = useCallback(async (username, password) => {
    const res = await api.login(username, password);
    localStorage.setItem('ri_loggedin', '1');
    localStorage.setItem('ri_user', res.nombre);
    localStorage.setItem('ri_rol', res.rol||'admin');
    setAuth({ ok:true, nombre:res.nombre, rol:res.rol||'admin' });
    await loadAll();
  }, [loadAll]);

  // ── Logout ──
  const logout = useCallback(() => {
    api.logout().catch(() => {});
    clearSession();
    localStorage.removeItem('ri_loggedin');
    setAuth({ ok:false, nombre:'', rol:'' });
    setData(emptyData());
  }, []);

  // ── Cambiar password ──
  const cambiarPassword = useCallback(async (actual, nueva) => {
    await api.cambiarPw(actual, nueva);
  }, []);

  useEffect(() => {
    if (auth.ok) loadAll();
    else setLoading(false);
  }, [auth.ok, loadAll]);

  // ── updateConfig ──
  const updateConfig = useCallback(async (cfg) => {
    const merged = { ...data.config, ...cfg };
    setData(d => ({ ...d, config: merged }));
    await api.saveConfig(merged).catch(e => avisarErrorSync('configuración', e));
  }, [data.config]);

  // ── addItem ──
  const addItem = useCallback(async (key, item) => {
    let cambiosStock = [];
    setData(d => {
      let next = { ...d, [key]: [item, ...d[key]] };
      if (COLS_VENTA.includes(key)) {
        cambiosStock = calcularAjusteStock(d.articulos, [], item.lineas);
        if (cambiosStock.length) next.articulos = d.articulos.map(a => cambiosStock.find(c => c.id===a.id) || a);
      }
      return next;
    });
    try { await api.create(key, item); }
    catch(e) { avisarErrorSync(key, e); }
    for (const a of cambiosStock) { try { await api.update('articulos', a.id, a); } catch(e) { avisarErrorSync('ajuste de stock', e); } }
  }, []);

  // ── replaceItem ──
  const replaceItem = useCallback(async (key, item) => {
    let cambiosStock = [];
    setData(d => {
      let next = { ...d, [key]: d[key].map(x => x.id===item.id ? item : x) };
      if (COLS_VENTA.includes(key)) {
        const anterior = d[key].find(x => x.id === item.id);
        cambiosStock = calcularAjusteStock(d.articulos, anterior?.lineas, item.lineas);
        if (cambiosStock.length) next.articulos = d.articulos.map(a => cambiosStock.find(c => c.id===a.id) || a);
      }
      return next;
    });
    try { await api.update(key, item.id, item); }
    catch(e) { avisarErrorSync(key, e); }
    for (const a of cambiosStock) { try { await api.update('articulos', a.id, a); } catch(e) { avisarErrorSync('ajuste de stock', e); } }
  }, []);

  // ── deleteItem ──
  const deleteItem = useCallback(async (key, id) => {
    let cambiosStock = [];
    setData(d => {
      let next = { ...d, [key]: d[key].filter(x => x.id!==id) };
      if (COLS_VENTA.includes(key)) {
        const anterior = d[key].find(x => x.id === id);
        if (anterior) {
          cambiosStock = calcularAjusteStock(d.articulos, anterior.lineas, []);
          if (cambiosStock.length) next.articulos = d.articulos.map(a => cambiosStock.find(c => c.id===a.id) || a);
        }
      }
      return next;
    });
    try { await api.remove(key, id); }
    catch(e) { avisarErrorSync(key, e); }
    for (const a of cambiosStock) { try { await api.update('articulos', a.id, a); } catch(e) { avisarErrorSync('ajuste de stock', e); } }
  }, []);

  // ── nextNum ──
  const nextNum = useCallback((field) => {
    let n;
    setData(d => {
      n = d.config[field];
      const newCfg = { ...d.config, [field]: n+1 };
      api.saveConfig(newCfg).catch(() => {});
      return { ...d, config: newCfg };
    });
    return n;
  }, []);

  // ── resetData ──
  const resetData = useCallback(async () => {
    const fresh = emptyData();
    setData(fresh);
  }, []);

  // ── downloadBackup ──
  const downloadBackup = useCallback(async () => {
    const blob = await api.backup();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `rootinformatica-backup-${today()}.json`;
    a.click(); URL.revokeObjectURL(url);
  }, []);

  return (
    <CTX.Provider value={{
      data, loading, auth, loadAll,
      login, logout, cambiarPassword,
      updateConfig, addItem, replaceItem, deleteItem, nextNum,
      resetData, downloadBackup,
    }}>
      {children}
    </CTX.Provider>
  );
}

export function useStore() { return useContext(CTX); }
